/**
 * The API client.
 *
 * Every request carries the bearer token (§2.2) and the browser's IANA timezone (§5.3).
 * Doing both here means no call site can forget either, and a 401 anywhere clears the
 * stored token and re-prompts — §2.2's "Re-prompt on 401", in one place.
 *
 * No client touches SQLite (§15); this is the only way the PWA reads or writes anything.
 */

import { cacheRead, cacheWrite, setServingStale } from "./readCache";

const TOKEN_KEY = "efss.token";

export function getToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private browsing with storage disabled. The token prompt will simply reappear.
    return null;
  }
}

export function setToken(token: string): void {
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}

export function clearToken(): void {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Adopt a token handed over in the URL fragment (`#t=…`), used by the QR handoff.
 *
 * The fragment is never sent to the server, so the token cannot reach an access log. It is
 * stripped from the URL immediately so it does not sit in the phone's history either.
 *
 * Returns the token if one was present.
 */
export function adoptTokenFromUrl(): string | null {
  const hash = window.location.hash;
  if (!hash.startsWith("#t=")) return null;

  const token = decodeURIComponent(hash.slice(3));
  if (!token) return null;

  setToken(token);
  window.history.replaceState({}, "", window.location.pathname + window.location.search);
  return token;
}

/** The browser's IANA zone, e.g. "Europe/London" (§5.3). */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export class ApiError extends Error {
  // Declared explicitly rather than as a constructor parameter property: the tsconfig
  // sets `erasableSyntaxOnly`, which rules out syntax that emits runtime code.
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }

  get isAuth(): boolean {
    return this.status === 401;
  }

  /** `fetch` itself threw — the request never reached the server at all. No response, so no
   * real HTTP status; `0` is not a status any server can send, which is what makes it safe to
   * use as this case's marker. */
  get isOffline(): boolean {
    return this.status === 0;
  }
}

/** Fires when a request is rejected, so the app can return to the token prompt. */
export const UNAUTHORIZED_EVENT = "efss:unauthorized";

/** Fires after a successful `api.logout()`, so `AuthGate` (mounted once, well above the
 * settings page that actually holds the "Log out" button) can drop back to the login
 * screen without needing a prop or a context threaded through the whole tree — the same
 * "one place, no call site can forget" reasoning `UNAUTHORIZED_EVENT` already uses. */
export const LOGGED_OUT_EVENT = "efss:logged-out";

type Options = {
  method?: string;
  body?: unknown;
  token?: string;
  /**
   * This write may be queued and replayed when the server cannot be reached (§8.2).
   *
   * Opt-in per endpoint rather than blanket, and the requirement is exact: replaying the
   * identical request must be **safe and idempotent**. `PATCH` setting fields, `DELETE`,
   * reordering (which sends the whole order), setting the Next Action or a recurrence rule
   * all qualify — doing them twice lands where doing them once did. Creating something does
   * *not*, unless it carries a client-minted id, which is why those go through their own
   * `queueTask*` helpers instead. Token management is never queueable.
   */
  queueable?: boolean;
  /** Extra headers beyond the timezone/auth/content-type ones this function already sets —
   * currently only `Idempotency-Key` (§8.1, `captureQueue.ts`) needs this. */
  headers?: Record<string, string>;
  /** True when a 401 here would mean "that specific credential was wrong," not "your
   * session or token died" — either because the request never carried one to begin with
   * (`api.login`/`api.register`) or because it's judging a *different* credential than the
   * one that authenticated the request itself (`api.changePassword`'s current-password
   * check). Either way it must not clear a stored token, fire `UNAUTHORIZED_EVENT`, or hide
   * the server's own detail — "Wrong username or password.", "Current password is incorrect."
   * — behind the generic re-prompt text every other 401 gets. */
  unauthenticated?: boolean;
};

/**
 * Reads that are never mirrored locally.
 *
 * `/api/health` drives the footer's "what version is actually running" (D64) — a cached
 * answer there would be the one lie that display exists to prevent. `/api/tokens` and
 * `/api/models` are both credential management: admin-only, never needed offline, and
 * keeping a copy in `localStorage` buys nothing worth the exposure.
 */
const NEVER_MIRRORED = ["/api/health", "/api/tokens", "/api/models"];

/** How long to wait for a server that may not be there, when the answer is already mirrored
 * locally. Long enough not to trip on an ordinary slow request; far short of the OS's own
 * connect timeout, which is what made offline feel sluggish (D167). */
const OFFLINE_GIVE_UP_MS = 2500;

function mirrorable(path: string, method: string): boolean {
  return method === "GET" && !NEVER_MIRRORED.some((prefix) => path.startsWith(prefix));
}

/** Whether this request could be answered locally if the network does not come through. */
function hasMirrorFor(path: string, method: string): boolean {
  return mirrorable(path, method) && mirroredFor(path) !== undefined;
}

export async function request<T>(path: string, options: Options = {}): Promise<T> {
  const method = options.method ?? "GET";
  const token = options.token ?? getToken();
  const headers: Record<string, string> = {
    "X-Timezone": browserTimezone(),
    ...options.headers,
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers["Content-Type"] = "application/json";

  const parkable = options.queueable === true && queueWrite !== null;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      // Give up quickly when there is somewhere local for this to go.
      //
      // A dropped *connection* rejects immediately, but an unreachable *route* — a VPN that
      // is off, a server that is down, wifi that associates but does not forward — leaves the
      // request hanging until the OS gives up on the TCP connect, which is tens of seconds.
      // That is the lag: not the mirror being slow, but the app waiting to be told what it
      // could already answer.
      //
      // Applies to a **queueable write** as much as to a mirrored read, which it did not at
      // first — so reads fell back in 2.5s while every completion, star and edit sat there
      // for half a minute, which is what "everything is taking 30s for these actions" was
      // (D170). Either way the principle is the same: when there is a local answer or a queue
      // to park in, waiting longer buys nothing. With neither, the request is left to run its
      // natural course rather than failing early on a slow but working link.
      signal:
        parkable || hasMirrorFor(path, method)
          ? AbortSignal.timeout(OFFLINE_GIVE_UP_MS)
          : undefined,
    });
  } catch {
    // `fetch` rejects (a raw `TypeError`, browser-worded and not fit to show anyone) when
    // there is no connection at all, as distinct from every other branch below, which needs
    // a real response to have arrived. Every existing catch block in the app already displays
    // `err.message` on a caught error (QuickAddCard, TaskDrawer, ...), so turning this into a
    // clear, consistent `ApiError` here — rather than a new error type callers would all need
    // to special-case — is what makes "you're offline" show up everywhere for free.
    if (parkable) return (await queueWrite!({ method, path, body: options.body })) as T;
    return offline<T>(path, method);
  }

  if (response.status === 502 || response.status === 503 || response.status === 504) {
    // *A* server answered — SWAG in production, Vite's dev proxy locally — but the app behind
    // it didn't. Confirmed directly, not assumed: Vite's proxy responds to an unreachable
    // target with a real 502, not a rejected `fetch`, so a backend that's down while the dev
    // server stays up looked like "the server said no" rather than "can't reach it" — every
    // offline-queued action failed to queue at all. From here a 502/503/504 is indistinguishable
    // from no connection: retrying the identical request is exactly as pointless either way,
    // and nothing in this app ever returns one of these three on purpose (no rate limiting, no
    // maintenance mode), so seeing one always means "can't currently reach EFSS," never "EFSS
    // said no." See DECISIONS.md D159.
    if (parkable) return (await queueWrite!({ method, path, body: options.body })) as T;
    return offline<T>(path, method);
  }

  if (response.status === 401 && !options.unauthenticated) {
    // Only clear the stored token when it was the stored token that failed — otherwise a
    // bad paste in the prompt would wipe a working one.
    if (!options.token) {
      clearToken();
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    }
    throw new ApiError(401, "That token was not accepted.");
  }

  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (typeof body?.detail === "string") detail = body.detail;
      else if (Array.isArray(body?.detail) && body.detail[0]?.msg) detail = body.detail[0].msg;
    } catch {
      /* keep the status line */
    }
    throw new ApiError(response.status, detail);
  }

  if (response.status === 204) return undefined as T;
  const body = (await response.json()) as T;
  if (mirrorable(path, method)) {
    cacheWrite(path, body);
    setServingStale(false);
  }
  return body;
}

/**
 * What a read means when the server cannot be reached: whatever the app last saw.
 *
 * This is the whole of "offline mode" for reads, and it lives here rather than in each card
 * on purpose — every read in the app already passes through `request()`, so doing it once
 * here is what makes offline stop being a per-card special case (D166).
 *
 * A *write* still throws, unchanged: the caller has to know it did not land so it can queue
 * it (`taskQueue.ts`). And a read the app has never made before still throws, because the
 * honest answer to "what are my tasks?" when it has never been told is "I can't reach the
 * server", not an empty list — which would read as "you have none".
 */
/**
 * How a queueable write is parked when the server is unreachable.
 *
 * Registered by `taskQueue.ts` at import time rather than imported here, because that module
 * already imports this one — dependency injection keeps the arrow pointing one way. Null
 * until then, which simply means a write fails as it always did.
 */
let queueWrite:
  ((req: { method: string; path: string; body?: unknown }) => Promise<unknown>) | null = null;

export function setOfflineWriteQueue(handler: typeof queueWrite): void {
  queueWrite = handler;
}

function offline<T>(path: string, method: string): T {
  if (mirrorable(path, method)) {
    const cached = mirroredFor<T>(path);
    if (cached !== undefined) {
      setServingStale(true);
      return cached;
    }
  }
  throw new ApiError(0, "You appear to be offline. This needs a connection to try again.");
}

/**
 * The mirrored answer for `path`: the exact request if it was ever made, or one computed
 * locally from a broader snapshot if this app knows how (see `setLocalAnswer`).
 *
 * **Never a different query's answer.** An earlier version fell back to any cached variant of
 * the same endpoint, on the reasoning that a query is "usually a filter" so a neighbour is at
 * worst too broad. That was wrong, and wrong in the most damaging way — silently returning
 * *someone else's data*: unfolding any area showed whichever area had been opened first,
 * every Wins range showed the range last viewed, and Blackjack's search kept answering with
 * the previous term's results. Plausible-looking wrong data is far worse than a gap, and it
 * is exactly what §0 rules out. Removed (D169).
 *
 * What that fallback was covering for is handled properly instead: a query that selects a
 * subset of something already mirrored is *computed* from it, by code that knows what the
 * parameters mean, rather than guessed at by key-prefix.
 */
function mirroredFor<T>(path: string): T | undefined {
  const exact = cacheRead<T>(path);
  if (exact !== undefined) return exact;
  return localAnswer === null ? undefined : (localAnswer(path) as T | undefined);
}

/**
 * Answers a read from a broader mirrored snapshot — registered by `offlineMirror.ts`, which
 * knows what each query parameter means, rather than being imported here (that module already
 * imports this one).
 */
let localAnswer: ((path: string) => unknown | undefined) | null = null;

export function setLocalAnswer(handler: typeof localAnswer): void {
  localAnswer = handler;
}

export const api = {
  // Accounts, sessions and invites (SPEC.md §2.2, §11 item 16, DECISIONS.md D189). No
  // `token` override on any of these the way `api.tokens(includeRevoked?, token?)` takes
  // one — login,
  // register and bootstrap are all unauthenticated to begin with, and the session cookie
  // they leave behind is sent by the browser automatically on every request after (fetch's
  // default `credentials: "same-origin"`), so nothing here needs to touch a header.
  me: () => request<Me>("/api/auth/me"),
  login: (username: string, password: string) =>
    request<Me>("/api/auth/login", {
      method: "POST",
      body: { username, password },
      unauthenticated: true,
    }),
  register: (username: string, password: string, inviteCode: string) =>
    request<Me>("/api/auth/register", {
      method: "POST",
      body: { username, password, invite_code: inviteCode },
      unauthenticated: true,
    }),
  logout: async () => {
    const result = await request<{ logged_out: boolean }>("/api/auth/logout", {
      method: "POST",
    });
    // `request()` attaches a stored bearer token to every call, session or not (it's how a
    // paired device authenticates at all) — so a token this browser ever adopted (the QR
    // handoff, `adoptTokenFromUrl`, or a leftover from before sessions existed) would
    // otherwise survive logout and silently re-authenticate the very next request, session
    // cookie or not. Logging out means this browser holds no credential at all afterward.
    clearToken();
    window.dispatchEvent(new Event(LOGGED_OUT_EVENT));
    return result;
  },
  updateUsername: (username: string) =>
    request<Me>("/api/auth/me", { method: "PATCH", body: { username } }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ changed: boolean }>("/api/auth/password", {
      method: "PUT",
      body: { current_password: currentPassword, new_password: newPassword },
      // A 401 here means "that's not your current password," not "your session died" —
      // same reasoning as api.login's own `unauthenticated`, and the same bug class: it
      // must not clear a stored token, fire UNAUTHORIZED_EVENT, or hide the server's real
      // "Current password is incorrect." behind the generic re-prompt message.
      unauthenticated: true,
    }),
  /** Deletes your own account, right now, no undo — the confirmation is the caller's job
   * (`AccountCard`'s `window.confirm`). Fires `LOGGED_OUT_EVENT` on success, same as
   * `api.logout()` — the account's own session is gone along with everything else. */
  deleteAccount: async () => {
    const result = await request<{ id: string; deleted: boolean }>("/api/auth/me", {
      method: "DELETE",
    });
    // Same reasoning as api.logout() above — a stored bearer token would otherwise outlive
    // the account it belonged to.
    clearToken();
    window.dispatchEvent(new Event(LOGGED_OUT_EVENT));
    return result;
  },

  // Invites — `AdminDep` server-side (§2.2), so these 403 for anyone but the owner.
  invites: (includeUsed = false) =>
    request<{ invites: Invite[] }>(
      `/api/auth/invites${includeUsed ? "?include_used=true" : ""}`,
    ),
  createInvite: (username: string) =>
    request<Invite & { code: string }>("/api/auth/invites", {
      method: "POST",
      body: { username },
    }),
  revokeInvite: (id: string) =>
    request<{ id: string; revoked: boolean }>(`/api/auth/invites/${id}`, { method: "DELETE" }),

  // Sessions — every login, browser cookie or otherwise, that's ever authenticated this
  // account. Own-account scoped like tokens, not admin-only.
  sessions: (includeRevoked = false) =>
    request<{ sessions: SessionInfo[] }>(
      `/api/auth/sessions${includeRevoked ? "?include_revoked=true" : ""}`,
    ),
  revokeSession: (id: string) =>
    request<{ id: string; revoked_at: number; already_revoked: boolean }>(
      `/api/auth/sessions/${id}`,
      { method: "DELETE" },
    ),

  /** Vouch for a watch showing this code — mints it a token under this account, the same
   * thing `createToken` already does, just reached by typing a code instead of a form
   * (`routers/auth.py::approve_device`, D200). `WriteDep`, not admin: approving a device is
   * minting a token for your own account. */
  approveDevice: (userCode: string, name?: string) =>
    request<{ approved: boolean; name: string }>("/api/auth/device/approve", {
      method: "POST",
      body: name ? { user_code: userCode, name } : { user_code: userCode },
    }),

  briefing: (energy?: number, includeSnoozed = false) => {
    const params = new URLSearchParams();
    if (energy !== undefined) params.set("energy", String(energy));
    if (includeSnoozed) params.set("include_snoozed", "true");
    const suffix = params.toString();
    return request<Briefing>(`/api/briefing${suffix ? `?${suffix}` : ""}`);
  },
  openTasks: (energy?: number, includeSnoozed = false) => {
    const params = new URLSearchParams();
    if (energy !== undefined) params.set("energy", String(energy));
    if (includeSnoozed) params.set("include_snoozed", "true");
    const suffix = params.toString();
    return request<OpenTasks>(`/api/briefing/open${suffix ? `?${suffix}` : ""}`);
  },
  /**
   * Every open task, for the offline mirror (§8.2): no energy ceiling, no cap, and no
   * `surfaced` bookkeeping — so the client can filter and search the whole list while
   * offline without a background sync inflating the Seen badge (D167).
   */
  openTasksSnapshot: (includeSnoozed = false) =>
    request<OpenTasks>(
      `/api/briefing/open?snapshot=true${includeSnoozed ? "&include_snoozed=true" : ""}`,
    ),
  starredTasks: () => request<{ tasks: Task[] }>("/api/briefing/starred"),
  energy: () => request<{ value: number }>("/api/settings/energy"),
  setEnergy: (value: number) =>
    request<{ value: number }>("/api/settings/energy", { method: "PUT", body: { value } }),

  tasks: (query: Record<string, string | number | undefined> = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== "") params.set(key, String(value));
    }
    const suffix = params.toString();
    return request<{ tasks: Task[]; count: number }>(
      `/api/tasks${suffix ? `?${suffix}` : ""}`,
    );
  },
  task: (id: string) => request<Task>(`/api/tasks/${id}`),
  createTask: (body: TaskCreateBody) =>
    request<Task | InboxFiled>("/api/tasks", { method: "POST", body }),
  patchTask: (id: string, body: Partial<Task> & { status?: Task["status"] }) =>
    request<Task>(`/api/tasks/${id}`, { method: "PATCH", body, queueable: true }),
  completeTask: (id: string) =>
    request<{ task: Task }>(`/api/tasks/${id}/complete`, {
      method: "POST",
    }),
  deleteTask: (id: string) =>
    request<{ id: string; deleted: boolean; events_kept: number }>(`/api/tasks/${id}`, {
      method: "DELETE",
      queueable: true,
    }),
  deferTask: (id: string, until?: string) =>
    request<Task>(`/api/tasks/${id}/defer`, {
      method: "POST",
      body: { until: until ?? null },
    }),

  // Subtasks and Next Action (§8.6). `createSubtasks` doubles as split when `id` already
  // names a subtask — the server redirects (§10) so one control serves both.
  //
  // `ids`, one per title, makes a replay idempotent (§5.1, §8.2) — `taskQueue.ts`'s offline
  // outbox use case. Omitted by every caller that isn't replaying a queued breakdown.
  createSubtasks: (id: string, titles: string[], ids?: string[]) =>
    request<{ subtasks: Task[] }>(`/api/tasks/${id}/subtasks`, {
      method: "POST",
      body: { titles, ids: ids ?? null },
    }),
  /** Nothing in the app calls this — every "Break down" control goes through
   * `createSubtasks` and lets the server decide whether it's a split (§8.6.2). Kept because
   * it names a real route `/api/docs` exposes, but deliberately without the `ids` parameter
   * `createSubtasks` carries: no caller can reach it, so it would be untestable dead weight. */
  splitSubtask: (id: string, titles: string[]) =>
    request<{ subtasks: Task[] }>(`/api/tasks/${id}/split`, {
      method: "POST",
      body: { titles },
    }),
  /** AI breakdown (§11 item 9b, §8.7) — same dual-purpose dispatch as `createSubtasks`
   * (parent's first children, or a subtask split in place), decided server-side by whether
   * `id` already has a parent. Lives on its own route (`routers/breakdown.py`) because it's
   * the one task-mutating call that talks to a model rather than only SQLite. Online-only:
   * there's no queueable shape for "ask a model something" the way there is for a titles
   * list already typed out (§8.2, D157).*/
  aiBreakdown: (id: string, tier: Tier) =>
    request<{ subtasks: Task[] }>(`/api/tasks/${id}/breakdown`, {
      method: "POST",
      body: { tier },
    }),
  /** Outline a project as a few larger tasks in it (D223). Online-only, like `aiBreakdown`. */
  aiProjectBreakdown: (id: string, tier: Tier) =>
    request<{ tasks: Task[] }>(`/api/projects/${id}/breakdown`, {
      method: "POST",
      body: { tier },
    }),
  /** Take back a project outline: the tasks it made are deleted unless worked on since. */
  undoProjectBreakdown: (id: string, created: string[]) =>
    request<{ removed: string[]; kept: string[] }>(`/api/projects/${id}/undo-breakdown`, {
      method: "POST",
      body: { created },
    }),
  /** Take back a breakdown: remove the steps it created, and for a split put back the one
   * it replaced (§8.6.2). Queueable — replaying it is safe by design, since an undo that
   * already landed returns the current steps. */
  undoBreakdown: (
    parentId: string,
    body: { created: string[]; restore_id?: string; title?: string; sort_order?: number },
  ) =>
    request<{ subtasks: Task[] }>(`/api/tasks/${parentId}/undo-breakdown`, {
      method: "POST",
      body,
      queueable: true,
    }),
  reorderSubtasks: (parentId: string, ids: string[]) =>
    request<{ subtasks: Task[] }>(`/api/tasks/${parentId}/order`, {
      method: "PATCH",
      body: { ids },
      queueable: true,
    }),
  setNextAction: (parentId: string, subtaskId: string) =>
    request<Task>(`/api/tasks/${parentId}/next-action`, {
      method: "POST",
      body: { subtask_id: subtaskId },
      queueable: true,
    }),

  // Recurrence and occurrences (§4.6, §7.1, §8.7).
  setRecurrence: (id: string, body: RecurrenceRequest) =>
    request<Task>(`/api/tasks/${id}/recurrence`, { method: "POST", body, queueable: true }),
  taskHistory: (id: string) => request<TaskHistory>(`/api/tasks/${id}/history`),
  /** The exact, uncapped streak (DECISIONS.md D141) — only called on demand, when a
   * reader clicks a capped `×N+` badge. */
  taskStreak: (id: string) =>
    request<{ task_id: string; streak: number }>(`/api/tasks/${id}/streak`),
  recurring: (window: "today" | "upcoming" = "today") =>
    request<RecurringWindow>(`/api/recurring?window=${window}`),
  /** `completedDate` backdates the completion — for reclaiming a `missed` occurrence
   * (DECISIONS.md D140) as of when the work actually happened, not when it was logged. */
  completeOccurrence: (
    taskId: string,
    dueDate: string,
    note?: string,
    completedDate?: string,
  ) =>
    request<OccurrenceActionResult>(`/api/occurrences/${taskId}/${dueDate}/complete`, {
      method: "POST",
      body: { note: note ?? null, completed_date: completedDate ?? null },
    }),
  skipOccurrence: (taskId: string, dueDate: string, note?: string) =>
    request<OccurrenceActionResult>(`/api/occurrences/${taskId}/${dueDate}/skip`, {
      method: "POST",
      body: { note: note ?? null },
    }),
  /** Corrects an already-resolved occurrence to missed — for a complete/skip tapped in
   * error (DECISIONS.md D145). Never the current pending occurrence; that's what
   * `completeOccurrence`/`skipOccurrence` are for. */
  missOccurrence: (taskId: string, dueDate: string) =>
    request<OccurrenceActionResult>(`/api/occurrences/${taskId}/${dueDate}/miss`, {
      method: "POST",
      queueable: true,
    }),
  /** Undoes a complete or skip — the tick as its own undo (§8.7). */
  reopenOccurrence: (taskId: string, dueDate: string) =>
    request<{ task: Task; occurrence: Occurrence }>(
      `/api/occurrences/${taskId}/${dueDate}/reopen`,
      { method: "POST", queueable: true },
    ),

  /** The task editor's AI bulb: the form's changed values go to the AI and the answer is written
   * onto this same task. `failed` writes nothing. Not queueable — it needs the model. */
  refineTask: (id: string, body: TaskRefineBody) =>
    request<RefineResult<Task>>(`/api/tasks/${id}/refine`, { method: "POST", body }),

  inbox: () => request<{ items: InboxItem[]; unresolved_count: number }>("/api/inbox"),
  /** Runs one inbox parse against the model now and waits for its answer. A model failure is
   * a 200 with `outcome: "failed"` — a 502 would read as "EFSS unreachable" (D159). */
  retryInboxAi: (id: string, tier?: CaptureTier | null) =>
    request<InboxAiRetryResult>(`/api/inbox/${id}/retry-ai`, {
      method: "POST",
      ...(tier ? { body: { tier } } : {}),
    }),
  /** Hands an item to the AI the way the Add card would, using the Create-task form's values.
   * Resolves the old item whatever the AI says, so unlike `retryInboxAi` an unsure or failed
   * answer leaves a *new* Inbox item, not the same one. Not queueable: it creates and resolves,
   * and the outbox does not chain replayed calls (D154, D157). */
  refineInboxAi: (id: string, body: InboxRefineBody) =>
    request<InboxAiRetryResult>(`/api/inbox/${id}/refine`, { method: "POST", body }),
  /** The Add box's front door (D224): the entries typed or captured and not yet acknowledged, each
   * with a `state` and, once filed, what it `created`. */
  justAdded: () => request<{ items: JustAddedEntry[] }>("/api/just-added"),
  /** An Add box entry under a chip (D222). Answers at once with the entry; the AI works in the
   * background and the list shows what it became. Queueable: the client-minted `id` makes a replay
   * the same entry. */
  justAddedAdd: (body: JustAddedBody) =>
    request<JustAddedEntry>("/api/just-added", { method: "POST", body }),
  /** The person has seen these (all of them, if no ids): filed ones are deleted, unplaced ones move
   * to the Inbox, and any the AI is still on stay. */
  acknowledgeJustAdded: (ids?: string[]) =>
    request<{ filed: number; to_inbox: number; kept: number }>("/api/just-added/acknowledge", {
      method: "POST",
      body: ids ? { ids } : {},
    }),
  /** An entry's form, saved: the Inbox's resolve, on the entry, which stays listed as filed. */
  resolveJustAdded: (id: string, body: unknown) =>
    request<{ id: string; resolved_to: string } & Record<string, unknown>>(
      `/api/just-added/${id}/resolve`,
      { method: "POST", body },
    ),
  /** The Inbox's lightbulb on an entry: run it against the model now. */
  retryJustAdded: (id: string, tier: CaptureTier | null) =>
    request<InboxAiRetryResult>(`/api/just-added/${id}/retry-ai`, {
      method: "POST",
      body: tier ? { tier } : {},
    }),
  /** The bulb on a form in an entry's row: hand it to the AI with what was typed. */
  refineJustAdded: (id: string, body: InboxRefineBody) =>
    request<InboxAiRetryResult>(`/api/just-added/${id}/refine`, { method: "POST", body }),
  /** How the AI's last attempt at an item went — for a queued item the worker finished while it
   * was on screen. 404 when there is nothing the AI did to report. */
  inboxResult: (id: string) => request<InboxAiRetryResult>(`/api/inbox/${id}/result`),
  resolveInbox: (id: string, body: unknown) =>
    // Shape depends on `action`: task/area/project/note each return their own entity
    // under a differently-named key, so the caller narrows by the action it sent.
    request<{ id: string; resolved_to: string } & Record<string, unknown>>(
      `/api/inbox/${id}/resolve`,
      { method: "POST", body },
    ),

  areas: (status = "active") => request<{ areas: Area[] }>(`/api/areas?status=${status}`),
  createArea: (body: { name: string; stale_after_days?: number; id?: string }) =>
    request<Area>("/api/areas", { method: "POST", body }),
  reorderAreas: (ids: string[]) =>
    request<{ areas: Area[] }>("/api/areas/order", {
      method: "PUT",
      body: { ids },
      queueable: true,
    }),
  patchArea: (id: string, body: Record<string, unknown>) =>
    request<Area>(`/api/areas/${id}`, { method: "PATCH", body, queueable: true }),
  deleteArea: (id: string) =>
    request<{ id: string; deleted: boolean }>(`/api/areas/${id}`, {
      method: "DELETE",
      queueable: true,
    }),

  projects: (status = "active") =>
    request<{ projects: Project[] }>(`/api/projects?status=${status}`),
  createProject: (body: {
    name: string;
    area_id: string;
    stale_after_days?: number;
    id?: string;
  }) => request<Project>("/api/projects", { method: "POST", body }),
  patchProject: (id: string, body: Record<string, unknown>) =>
    request<Project>(`/api/projects/${id}`, { method: "PATCH", body, queueable: true }),
  completeProject: (id: string) =>
    request<Project>(`/api/projects/${id}/complete`, { method: "POST", queueable: true }),
  /** The project popup's AI bulb (D222): tidy the name, keep (or move to) the area sent. */
  refineProject: (id: string, body: ProjectRefineBody) =>
    request<RefineResult<Project>>(`/api/projects/${id}/refine`, { method: "POST", body }),
  deleteProject: (id: string) =>
    request<{ id: string; deleted: boolean }>(`/api/projects/${id}`, {
      method: "DELETE",
      queueable: true,
    }),

  notes: () => request<{ notes: Note[] }>("/api/notes"),
  createNote: (body: {
    title: string;
    body?: string | null;
    area_id?: string | null;
    project_id?: string | null;
    id?: string;
  }) => request<Note>("/api/notes", { method: "POST", body }),
  /** The note form's AI bulb on a new note: put to the AI now, and waited for. Answers like
   * `retryInboxAi`: `filed` (the note exists, in `created`) or `failed` (the note is kept in the
   * Inbox as a note, for the worker to try again). */
  createNoteWithAi: (body: {
    title: string;
    body?: string | null;
    area_id?: string | null;
    project_id?: string | null;
    tier?: CaptureTier | null;
    id?: string;
  }) => request<InboxAiRetryResult>("/api/notes/ai", { method: "POST", body }),
  /** The note editor's AI bulb: the form's values go to the AI and the answer is written onto
   * this same note. `failed` writes nothing. Not queueable — it needs the model. */
  refineNote: (id: string, body: NoteRefineBody) =>
    request<RefineResult<Note>>(`/api/notes/${id}/refine`, { method: "POST", body }),
  note: (id: string) => request<Note>(`/api/notes/${id}`),
  patchNote: (id: string, body: Record<string, unknown>) =>
    request<Note>(`/api/notes/${id}`, { method: "PATCH", body, queueable: true }),
  deleteNote: (id: string) =>
    request<{ id: string; deleted: boolean }>(`/api/notes/${id}`, {
      method: "DELETE",
      queueable: true,
    }),

  wins: (range: WinRange) => request<Wins>(`/api/wins?range=${range}`),
  /** `includeNextActionSubtasks` is Blackjack's own opt-in (§9.2, §8.6.4) — every other
   * reader leaves it off, so GoneQuiet's own list is unaffected. */
  slipping: (includeNextActionSubtasks = false) =>
    request<Slipping>(
      `/api/slipping${includeNextActionSubtasks ? "?include_next_action_subtasks=true" : ""}`,
    ),

  // Blackjack has no endpoints: §9 is ephemeral and runs entirely in the browser. The only
  // write it makes is `createTask`, when a slot holds a freshly typed title.

  tokens: (includeRevoked = false, token?: string) =>
    request<{ tokens: TokenInfo[] }>(
      `/api/tokens${includeRevoked ? "?include_revoked=true" : ""}`,
      { token },
    ),
  createToken: (name: string, scopes: string[]) =>
    request<TokenInfo & { token: string }>("/api/tokens", {
      method: "POST",
      body: { name, scopes },
    }),
  revokeToken: (id: string) =>
    request<{ id: string; revoked_at: number; already_revoked: boolean }>(
      `/api/tokens/${id}`,
      { method: "DELETE" },
    ),

  // Model configuration (§6.2, D182, D211). Each account may add, edit and delete their
  // own connections; the list also includes the admin's. Credential-bearing, so never
  // queued or mirrored offline, same as tokens — but no longer admin-only across the board.
  modelConfigs: () => request<{ configs: ModelConfig[] }>("/api/models/configs"),
  createModelConfig: (body: ModelConfigInput) =>
    request<ModelConfig>("/api/models/configs", { method: "POST", body }),
  updateModelConfig: (id: string, body: Partial<ModelConfigInput>) =>
    request<ModelConfig>(`/api/models/configs/${id}`, { method: "PATCH", body }),
  deleteModelConfig: (id: string) =>
    request<{ id: string; name: string; deleted: boolean }>(`/api/models/configs/${id}`, {
      method: "DELETE",
    }),
  modelSlots: () => request<ModelSlots>("/api/models/slots"),
  setModelSlots: (body: ModelSlotUpdate) =>
    request<ModelSlots>("/api/models/slots", { method: "PUT", body }),
  /** `GET {base_url}/models`, proxied server-side — the browser often can't reach a LAN
   * host like colossus directly. `apiKey` is sent for this one call only, never stored.
   * The returned `base_url` is normalized (`/v1` appended if it was missing) — the same
   * correction `createModelConfig`/`updateModelConfig` apply before storing, surfaced here
   * so the form can show what will actually be saved. */
  discoverModels: (baseUrl: string, apiKey?: string) =>
    request<{ models: string[]; base_url: string }>("/api/models/discover", {
      method: "POST",
      body: { base_url: baseUrl, api_key: apiKey || undefined },
    }),

  // Live-editable system prompts (migration 0011) — reading is open to every account, but
  // `prompt_overrides` is one shared row per prompt used for everyone's captures (D210), so
  // setting one is admin-only, same as the model config calls above.
  prompts: () => request<{ prompts: PromptInfo[] }>("/api/prompts"),
  setPromptOverride: (name: string, body: string) =>
    request<{ name: string; override: string }>(`/api/prompts/${name}`, {
      method: "PUT",
      body: { body },
    }),

  // Each account's own preferred capture/enrichment tier (migration 0012, 0021) — picked
  // directly, not assigned through Settings/Models (that's for configuring which model
  // fills each slot).
  getCaptureTier: () => request<{ tier: CaptureTier }>("/api/capture-tier"),
  setCaptureTier: (tier: CaptureTier) =>
    request<{ tier: CaptureTier }>("/api/capture-tier", { method: "PUT", body: { tier } }),

  // Recent Activity tray (migration 0012) — where a submission landed and what AI did to it,
  // across Inbox (pending and resolved) and enrichment alike.
  activity: (limit?: number) =>
    request<{ items: ActivityItem[] }>(`/api/activity${limit ? `?limit=${limit}` : ""}`),
  /** "Dismiss all": hides exactly these entries (the ones on screen), not everything. */
  dismissActivities: (items: { kind: ActivityItem["kind"]; id: string }[]) =>
    request<{ dismissed: number; dismissed_at: number }>("/api/activity/dismiss", {
      method: "POST",
      body: { items },
    }),
  dismissActivity: (kind: ActivityItem["kind"], id: string) =>
    request<{ id: string; dismissed_at: number }>(`/api/activity/${kind}/${id}/dismiss`, {
      method: "POST",
    }),

  // Context — reference material curated for the AI layer (§8.13, D183). Personal, not
  // shared instance plumbing like model configs/prompts — each account owns and can only
  // see their own (item 17), even though it's surfaced on the same Models page.
  context: (scopeType?: ContextScopeType, scopeId?: string) => {
    const params = new URLSearchParams();
    if (scopeType) params.set("scope_type", scopeType);
    if (scopeId) params.set("scope_id", scopeId);
    const suffix = params.toString();
    return request<{ context: ContextEntry[] }>(`/api/context${suffix ? `?${suffix}` : ""}`);
  },
  createContext: (body: ContextInput) =>
    request<ContextEntry>("/api/context", { method: "POST", body }),
  updateContext: (
    id: string,
    body: { title?: string; body?: string; scope_type?: ContextScopeType; scope_id?: string | null },
  ) => request<ContextEntry>(`/api/context/${id}`, { method: "PATCH", body }),
  /** The Context popup's AI bulb (D222): tidy the text, keep the scope sent. */
  refineContext: (id: string, body: ContextRefineBody) =>
    request<RefineResult<ContextEntry>>(`/api/context/${id}/refine`, { method: "POST", body }),
  deleteContext: (id: string) =>
    request<{ id: string; deleted: boolean }>(`/api/context/${id}`, { method: "DELETE" }),

  health: () => request<Health>("/api/health"),

  /**
   * §8.1 fire-and-forget capture, called by `captureQueue.ts`. Written before `/api/capture`
   * existed, so a failure here is handled like any other unreachable endpoint, which the
   * queue treats as "try again later" — see DECISIONS.md D151.
   *
   * `source` is always `"web"` here; `"watch"` and `"cli"` are the other two clients §8.1
   * names, neither of which goes through this PWA.
   */
  capture: (text: string, idempotencyKey: string) =>
    request<{ id: string }>("/api/capture", {
      method: "POST",
      body: { text, source: "web" },
      headers: { "Idempotency-Key": idempotencyKey },
    }),
};

// ----------------------------------------------------------------------------------------
// Types — mirror the backend payloads
// ----------------------------------------------------------------------------------------

/**
 * What the client knew when the text was written down (§8.1) — carried on `POST /api/tasks`
 * and read **only** if that call ends up filing to the inbox. An entry with a destination
 * becomes a task, and `tasks` has nowhere to put a position, so these are silently ignored
 * there rather than rejected. `position.ts` decides when it is worth gathering one.
 */
export type CaptureContext = {
  lat?: number;
  lon?: number;
  captured_at?: string;
};

export type TaskCreateBody = Partial<Task> & { title: string } & CaptureContext & {
    /** The Add card bulb's own tier, stamped on whatever the AI is later handed (migration
     * 0023). Omitted, the account's capture tier applies. */
    tier?: CaptureTier;
    /** The Add box's Task chip: the person chose Task, so the AI only routes it (D222). */
    kind?: "task";
    /** The Add box sent this (D224): it leaves a row in Just added, made or held for the AI. */
    just_added?: boolean;
  };

/** Why the parse worker (SPEC.md §11 item 7b) routed a capture the way it did, present only
 * on a task or note it created itself. Hidden by default in the UI — surfaced so the owner
 * can see what Context entries (§8.13) would help. */
export type AiReasoning = {
  reason: string | null;
  model_key: string | null;
  slot: string | null;
};

export type Task = {
  id: string;
  title: string;
  notes: string | null;
  status: "open" | "done" | "dropped";
  area_id: string;
  project_id: string | null;
  /** Set on a subtask, one level only (§8.6) — a task with a `parent_id` cannot itself be a
   * parent, so this is never chained more than once. */
  parent_id: string | null;
  /** Position among subtask siblings, ascending. `null` on anything that isn't a subtask. */
  sort_order: number | null;
  /** The one subtask per parent that's actionable right now (§8.6.1). Always `false` on a
   * task with no `parent_id`. */
  is_next_action: boolean;
  energy: number | null;
  important: boolean;
  starred: boolean;
  estimate_min: number | null;
  due: string | null;
  snoozed_until: string | null;
  /** RFC 5545, nullable. Null = one-off task. Set via `api.setRecurrence` (§4.6). */
  rrule: string | null;
  recur_anchor: "schedule" | "completion" | null;
  recur_start: string | null;
  created_at: number;
  updated_at: number;
  completed_at: number | null;
  last_touched_at: number;
  surfaced_count: number;
  deferred_count: number;
  starred_count: number;

  // Present on tasks served by the briefing, which computes them: §8.5's thresholds are
  // inherited from the project or area, so a client cannot work them out for itself.
  group?: "overdue" | "important" | "slipping" | "open";
  days_since_touch?: number;
  stale_after_days?: number;
  area_name?: string | null;
  project_name?: string | null;

  // §8.6.4: embedded on every top-level task (never on a subtask itself) — "reachable only
  // through their parent" means the parent's own response has to carry them.
  subtasks?: Task[];
  subtask_done_count?: number;
  subtask_total_count?: number;
  next_action_id?: string | null;

  // Present on `GET /api/tasks` and `GET /api/tasks/{id}`; absent (not `null`) on responses
  // from actions that don't look it up — `null` there just means "not present", not "there's
  // no reasoning", so this stays optional rather than always-required.
  ai_reasoning?: AiReasoning | null;
};

export type RecurrenceRequest = {
  rrule: string | null;
  recur_anchor: "schedule" | "completion" | null;
  recur_start: string | null;
};

/** One row of `task_occurrences` — a resolved outcome, or the single current pending one. */
export type Occurrence = {
  id: number;
  task_id: string;
  due_date: string;
  status: "pending" | "completed" | "skipped" | "missed";
  completed_at: number | null;
  completed_date: string | null;
  delta_days: number | null;
  tz: string | null;
  note: string | null;
};

export type OccurrenceActionResult = {
  task: Task;
  occurrence: Occurrence;
  next: Occurrence | null;
};

/**
 * One recurring task's row on the Recurring card (§8.7) — its single current occurrence.
 * There is only ever one live occurrence per task (§7.1.2), so `window=today` and
 * `window=upcoming` partition every recurring task by whether that one `due_date` has
 * arrived yet; a task appears in exactly one of the two, never both.
 */
export type RecurringEntry = {
  task_id: string;
  title: string;
  area_id: string;
  due_date: string;
  overdue: boolean;
  recur_anchor: "schedule" | "completion";
  /** The rule itself, RFC 5545. Never shown raw — badged through `recurrenceLabel` (§4.6.2). */
  rrule: string;
  /** Activation cost (§8.3) — scales the §8.4 completion celebration, same as any task. */
  energy: number | null;
  /** Rolling-30 completion rate, or null when there is nothing in the window to judge. */
  rate_30d: number | null;
  streak: number;
  /** True once the streak scan hit its bound without finding a break — the true streak is
   * at least `streak`, possibly longer (a floor, not an exact count past that point). */
  streak_capped: boolean;
};

export type RecurringWindow = {
  date: string;
  tz: string;
  window: "today" | "upcoming";
  occurrences: RecurringEntry[];
};

export type TaskHistory = {
  task_id: string;
  rrule: string | null;
  recur_anchor: "schedule" | "completion" | null;
  /** Most recent entries only (10), newest first — not the task's full lifetime. Full
   * history still drives `delta_histogram` below, computed server-side. */
  recent_occurrences: Occurrence[];
  rate_30d: number | null;
  streak: number;
  streak_capped: boolean;
  /** delta_days -> count of completions with that delta (§7.1.3's chart). */
  delta_histogram: Record<string, number>;
};

export type Area = {
  id: string;
  name: string;
  stale_after_days: number;
  status: string;
  sort_order: number;
  /** Open tasks filed directly under this area or under one of its projects — `tasks.area_id`
   * is set either way (§4). Absent from hand-built fixtures that never call the list route. */
  open_tasks?: number;
  /** Notes filed directly under this area. Feeds the archive confirmation's enumeration
   * (gitea #49, D206). Absent from hand-built fixtures that never call the list route. */
  notes_count?: number;
  /** Active child projects — archiving this area folds them away too (D206), so the
   * confirmation needs to say how many. Absent from hand-built fixtures. */
  active_projects?: number;
};

export type Project = {
  id: string;
  area_id: string;
  name: string;
  outcome: string | null;
  status: string;
  target_date: string | null;
  stale_after_days: number;
  /** Open tasks filed under this project. Absent from hand-built fixtures that never call
   * the list route. */
  open_tasks?: number;
  /** Notes filed under this project. Feeds the archive confirmation's enumeration (gitea
   * #49, D206). Absent from hand-built fixtures that never call the list route. */
  notes_count?: number;
  /** Only on the answer to the project popup's AI bulb (D222). */
  ai_reasoning?: AiReasoning | null;
};

export type Note = {
  id: string;
  title: string;
  body: string | null;
  area_id: string | null;
  project_id: string | null;
  promoted_path: string | null;
  created_at: number;
  updated_at: number;
  ai_reasoning: AiReasoning | null;
};

export type SlippingEntry = {
  id: string;
  name: string;
  days_since_touch: number;
  stale_after_days: number;
  over_by: number;
  area_name?: string | null;
  project_name?: string | null;
  open_tasks?: number;
  active_projects?: number;
  deferred_count?: number;
  energy?: number | null;
  /** Task entries only — what the briefing would badge this with. */
  due?: string | null;
  important?: boolean;
  starred?: boolean;
};

export type Slipping = {
  as_of: string;
  tz: string;
  tasks: SlippingEntry[];
  projects: SlippingEntry[];
  areas: SlippingEntry[];
};

export type WinRange = "today" | "yesterday" | "7d" | "30d";

export type Completion = {
  /** Tasks and finished projects share this list; a project is the bigger win (§8.4). */
  kind: "task" | "project";
  task_id: string;
  title: string;
  area_name: string | null;
  energy: number | null;
  outcome: string | null;
  completed_at: number;
  /** Set when this completion was a subtask's — shown as context in place of the area badge
   * (§8.6.4). `null` for a project or a task with no parent. */
  parent_id: string | null;
  parent_title: string | null;
};

export type Wins = {
  range: WinRange;
  from: string;
  to: string;
  counts: Record<WinRange, number>;
  completions: Completion[];
};

export type Briefing = {
  date: string;
  tz: string;
  inbox_count: number;
  energy: number;
  starred: Task[];
  open: Task[];
  open_total: number;
  open_truncated: boolean;
  snoozed_count: number;
  slipping: {
    counts: { tasks: number; projects: number; areas: number };
    tasks: SlippingEntry[];
    projects: SlippingEntry[];
    areas: SlippingEntry[];
  };
  wins: { counts: Record<WinRange, number>; completions: Completion[] };
  empty_state: { no_areas: boolean; no_tasks: boolean };
};

/** `GET /api/briefing/open` — the same section `Briefing.open` used to carry, servable on
 * its own (D107). `no_tasks` is the whole table, not just what's visible at this energy —
 * see that route's docstring. */
export type OpenTasks = {
  tasks: Task[];
  total: number;
  truncated: boolean;
  /** How many Slipping/Everything-else tasks exist in total, independent of `energy` below
   * — Overdue and Important bypass the ceiling entirely and carry their own counts in
   * their own group headings, so `total` (which counts all four groups) isn't the right
   * number to report next to the slider (§11). */
  open_eligible_total: number;
  snoozed_count: number;
  energy: number;
  no_tasks: boolean;
};

export type InboxItem = {
  id: string;
  raw_text: string;
  cleaned_text: string | null;
  source: string;
  guess_kind: string | null;
  guess_payload: Record<string, unknown> | null;
  /** Structured fields a human already typed — an Add-card entry with no area/project
   * (§11 item 7b). Never touched by the parse worker, unlike `guess_payload`. */
  entered_payload: Record<string, unknown> | null;
  /** What the client knew when it captured (§8.1) — `null` when it knew nothing, which is
   * not a degraded case. Same shape as `CaptureContext`, the request-side type: server and
   * client agree on what a half-fix means, so there is one definition rather than two. */
  client_context: CaptureContext | null;
  reason: string | null;
  created_at: number;
  resolved_at: number | null;
  /** Retry bookkeeping (§11 item 7b, migration 0010). `parsed_at` set once the worker has
   * produced a validated result, confident or not — it's what stops an uncertain result from
   * being retried forever. `parse_attempts`/`next_parse_at` only track real failures. */
  parse_attempts: number;
  parsed_at: number | null;
  next_parse_at: number | null;
  /** The tier of the bulb that made this (migration 0023); `null` when no bulb did, which
   * falls back to the account's capture tier. `"off"` leaves it for a person. */
  tier?: CaptureTier | null;
  /** Where it stands (set on the list and on a result read-back): `processing` (the AI is on it
   * now), `queued` (the AI will get to it), `unresolved` (it needs a person). */
  state?: InboxItemState;
  /** The plain-language reason the last attempt failed, if it did. */
  last_error?: string | null;
};

/** What the Create-task form held when its lightbulb was pressed. Neither `area_id` nor
 * `project_id` is allowed — "I don't know where this goes" is what the lightbulb is for. */
export type InboxRefineBody = {
  title: string;
  area_id?: string;
  project_id?: string;
  energy?: number | null;
  important?: boolean;
  starred?: boolean;
  due?: string | null;
  estimate_min?: number | null;
  notes?: string | null;
  /** The form bulb's own tier — see `bulbTier.ts`. */
  tier?: CaptureTier | null;
  /** On a Just added entry that is already filed ("Change to"): the AI's answer replaces what was made. */
  replaces?: boolean;
  /** `"note"` for the Save-note form's bulb: `notes` is then the note's body, and only the title,
   * body and area or project are read. `"project"`: `title` is the name, `area_id` optional.
   * `"context"`: `notes` is the text, with `scope_type`/`scope_id` (D222). */
  kind?: "task" | "note" | "project" | "context";
  scope_type?: ContextScopeType;
  scope_id?: string | null;
};

/** What the AI bulb in an editor answers. `updated`: `item` is the task or note as it now stands
 * and `changed` names the fields that moved. `failed`: nothing was written; `error` says why. */
export type RefineResult<T> = {
  outcome: "updated" | "failed";
  error: string | null;
  item: T | null;
  changed: string[];
};

/** The task editor's changed fields (the rest are read from the task) and its bulb's tier. */
export type TaskRefineBody = {
  title?: string;
  notes?: string | null;
  area_id?: string | null;
  project_id?: string | null;
  energy?: number | null;
  important?: boolean;
  starred?: boolean;
  estimate_min?: number | null;
  due?: string | null;
  tier?: CaptureTier | null;
};

export type ProjectRefineBody = { name?: string; area_id?: string; tier?: CaptureTier | null };

export type ContextRefineBody = {
  title?: string;
  body?: string;
  scope_type?: ContextScopeType;
  scope_id?: string | null;
  tier?: CaptureTier | null;
};

export type NoteRefineBody = {
  title?: string;
  body?: string | null;
  area_id?: string | null;
  project_id?: string | null;
  tier?: CaptureTier | null;
};

export type JustAddedBody = {
  kind: "auto" | "note" | "project" | "context";
  text: string;
  id?: string;
  tier?: CaptureTier;
  /** Note: the text under the title. */
  body?: string | null;
  /** Note destination, or the Project chip's area. */
  area_id?: string | null;
  project_id?: string | null;
  /** Context chip. */
  scope_type?: ContextScopeType;
  scope_id?: string | null;
};

export type InboxItemState = "queued" | "processing" | "unresolved";

/** Where a Just added entry stands: the AI is on it (`queued`, `processing`), something was made
 * (`filed`), the AI was unsure (`guess`) or gave up (`failed`), or nothing will run it (`held`). */
export type JustAddedState = "queued" | "processing" | "filed" | "guess" | "failed" | "held";

export type JustAddedEntry = Omit<InboxItem, "state"> & {
  state: JustAddedState;
  /** What a `filed` entry made. */
  created: InboxAiCreated | null;
  /** The AI is still polishing the task it made (title, energy, due...). */
  enriching: boolean;
};

export type InboxAiRetryResult = {
  item: InboxItem;
  /** `filed`: confident, became a task or note. `guess`: answered but unsure, left for you.
   * `failed`: the model errored or said something unusable — see `error`. */
  outcome: "filed" | "guess" | "failed";
  /** A sentence a person can act on — never the model server's own output. On a `filed`
   * refine it is the AI's failure to *polish* a task that exists regardless. */
  error: string | null;
  resolved_to: string | null;
  /** What `filed` made, in words (`null` otherwise). The AI only ever makes a task or a note. */
  created: InboxAiCreated | null;
};

export type InboxAiCreated = {
  kind: "task" | "note" | "project" | "context";
  id: string;
  title: string;
  area: string | null;
  project: string | null;
};

/**
 * A task with nowhere to go. The server returns 202 and holds the text rather than
 * guessing an area (§0) — see the `POST /api/tasks` docstring.
 */
export type InboxFiled = { filed_to: "inbox" | "just_added"; item: InboxItem };

export function wasFiledToInbox(result: Task | InboxFiled): result is InboxFiled {
  return "filed_to" in result;
}

export type TokenInfo = {
  id: string;
  name: string;
  scopes: string[];
  created_at: number;
  last_used_at: number | null;
  revoked_at: number | null;
};

/** `GET /api/auth/me` (and the same shape from bootstrap/register/login) — the logged-in
 * account. `is_admin` is what gates the Invites card client-side; the server enforces the
 * same thing independently (`AdminDep`), so this is a display convenience, not a security
 * boundary. */
export type Me = {
  id: string;
  username: string;
  is_admin: boolean;
  created_at: number;
};

/** An invite, plaintext `code` present only on the response from creating one — same
 * "shown once, never again" contract as `TokenInfo`'s minted value (§2.2). */
export type Invite = {
  id: string;
  username: string;
  created_at: number;
  expires_at: number;
  used_at: number | null;
};

/** A session — a login, cookie or otherwise, that's ever authenticated this account.
 * `current` is only ever true for the one that authenticated *this* request; a request
 * that came in on a bearer token instead has none flagged current, correctly, since none
 * of the listed sessions is what got it in. */
export type SessionInfo = {
  id: string;
  created_at: number;
  last_used_at: number | null;
  expires_at: number;
  revoked_at: number | null;
  current: boolean;
};

export type Health = {
  status: string;
  /** The application version — the single source in `backend/efss/__init__.py`. */
  version: string;
  schema_version: number;
  last_batch_run: string | null;
  /** `EFSS_AI_ENABLED` (§6.2, D182) — a container-level deployment choice, read-only here. */
  ai_enabled: boolean;
  /** What the parse worker's own selection query would pick up right now, plus anything
   * still backing off (§11 item 7b). `null` only when the database itself is degraded. */
  queue_depth: number | null;
  /** `free` | `shared` | `off` (§6.3, §11 item 10) — set by colossus, read-only here. */
  gpu_mode: string;
  now: number;
};

export type ModelProvider = "openai_compatible" | "anthropic";
export type VramClass = "light" | "heavy" | "none";

export type ModelConfig = {
  id: string;
  /** Who added this connection (D211) — compare against `Me.id` to know whether the
   * current account may edit or delete it. Every connection listed here is either the
   * caller's own or the admin's; never a different friend's. */
  created_by: string;
  name: string;
  provider: ModelProvider;
  model_key: string;
  base_url: string | null;
  /** Never the key itself — see `crypto.py`. Whether one is stored, nothing more. */
  has_api_key: boolean;
  vram_class: VramClass;
  ttl_seconds: number | null;
  notes: string | null;
  created_at: number;
  updated_at: number;
};

export type ModelConfigInput = {
  name: string;
  provider: ModelProvider;
  model_key: string;
  base_url?: string | null;
  /** Create: omit for none. Patch: omit to leave unchanged, `""` to clear, else replace. */
  api_key?: string | null;
  vram_class?: VramClass;
  ttl_seconds?: number | null;
  notes?: string | null;
};

export type Tier = "dim" | "on" | "bright";

export type ModelSlotSummary = { id: string; name: string; vram_class: VramClass } | null;

export type ModelSlots = {
  dim: ModelSlotSummary;
  on: ModelSlotSummary;
  bright: ModelSlotSummary;
};

export type ModelSlotUpdate = {
  dim?: string | null;
  on?: string | null;
  bright?: string | null;
};

/** A system prompt (migration 0011). `override` is `""` when this install hasn't customized
 * it — the Models page's edit field starts empty in that case, never pre-filled with
 * `default`, which is shown separately for reference. */
export type PromptInfo = {
  name: string;
  label: string;
  default: string;
  override: string;
};

/** Off/dim/on/bright, picked directly from the Add card (or wherever else the picker is
 * mounted) — the level the `Lightbulb` glyph itself already renders. */
export type CaptureTier = "off" | "dim" | "on" | "bright";

/** One entry in the Recent Activity tray — either a (pending or resolved) Inbox item, or a
 * routed task's enrichment pass. `landed.type === "inbox"` means still awaiting a human
 * routing decision; `"task"`/`"note"` means it's already filed. `entered` is what the human
 * typed themselves; `filled` is what AI added — for an unresolved inbox item this is the
 * model's raw proposal (`guess_payload`, previously never surfaced anywhere); for a resolved
 * one there's no reliable per-field record, so it's empty and `reason` is what's shown
 * instead. `tier` is what actually ran it; `preferred_tier` is what was selected at the time,
 * so a mismatch between the two is how a GPU-budget drop stays visible after the fact. */
export type ActivityItem = {
  /** `refined`: a task or note the AI edited in place from its editor (D220); `id` is its event. */
  kind: "inbox" | "enrichment" | "refined";
  id: string;
  raw_text: string;
  landed: {
    type: "task" | "note" | "area" | "project" | "context" | "inbox" | "discarded";
    id: string;
    title: string;
  };
  entered: Record<string, unknown>;
  filled: Record<string, unknown>;
  tier: CaptureTier | null;
  preferred_tier: CaptureTier | null;
  model_key: string | null;
  reason: string;
  created_at: number;
  dismissed_at: number | null;
};

/**
 * Context — persistent reference material curated for the AI layer (SPEC.md §8.13,
 * DECISIONS.md D183), read by the parse worker (§7b) and breakdown (§9b). Distinct from
 * `Note`: never promoted to the vault, always author-curated, and scoped deliberately
 * rather than inferred from `area_id`/`project_id` being set.
 */
export type ContextScopeType = "all" | "area" | "project";

export type ContextEntry = {
  id: string;
  scope_type: ContextScopeType;
  /** An area or project id, depending on `scope_type`. `null` for `scope_type: "all"`. */
  scope_id: string | null;
  title: string;
  body: string;
  created_at: number;
  updated_at: number;
  /** Only on the answer to the Context popup's AI bulb (D222). */
  ai_reasoning?: AiReasoning | null;
};

export type ContextInput = {
  scope_type: ContextScopeType;
  /** Required for `"area"`/`"project"`; must be omitted for `"all"`. */
  scope_id?: string | null;
  title: string;
  body: string;
  id?: string;
};
